/**
 * Client pour l'Edge Function `lookup-communes` du back-office.
 *
 * Deux scopes :
 *   - "reseau"   : communes routables (table `communes`). Alimente le dropdown
 *                  « Ville » du mode `cp`, dont le routage dépend du code INSEE.
 *   - "national" : référentiel complet (table `communes_ref`). Alimente le
 *                  sélecteur de commune du mode `magasin`, où le client doit
 *                  trouver sa commune même hors zone couverte.
 *
 * L'URL de l'endpoint est déduite de `VITE_SUBMIT_LEAD_URL` (on échange juste
 * le dernier segment) pour éviter d'avoir à maintenir une seconde env var.
 */

export interface Commune {
  code_insee: string;
  nom: string;
  /** Présent uniquement en scope national. */
  code_postal?: string;
}

export type CommuneScope = "reseau" | "national";

const CP_RE = /^[0-9]{5}$/;

export function resolveEndpoint(): string | null {
  const explicit = import.meta.env.VITE_LOOKUP_COMMUNES_URL;
  if (explicit) return explicit;

  const submit = import.meta.env.VITE_SUBMIT_LEAD_URL;
  if (!submit) return null;
  return submit.replace(/\/submit-lead(\/|$)/, "/lookup-communes$1");
}

/** Libellé affiché dans le sélecteur : « 13100 - Aix-en-Provence ». */
export function formatCommuneLabel(commune: Commune): string {
  return commune.code_postal ? `${commune.code_postal} - ${commune.nom}` : commune.nom;
}

// Cache in-memory (scope : vie du custom element). Évite de re-fetcher le
// même CP si l'utilisateur revient en arrière puis repasse sur l'étape.
// La clé porte le scope pour que les deux listes ne se mélangent pas.
const cache = new Map<string, Commune[]>();

export async function lookupCommunes(
  codePostal: string,
  scope: CommuneScope = "reseau",
  signal?: AbortSignal,
): Promise<Commune[]> {
  const cp = codePostal.trim();
  if (!CP_RE.test(cp)) return [];

  const cacheKey = `${scope}:${cp}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const endpoint = resolveEndpoint();
  if (!endpoint) {
    console.error("[lookupCommunes] missing VITE_SUBMIT_LEAD_URL / VITE_LOOKUP_COMMUNES_URL");
    return [];
  }

  const query = scope === "national" ? `?cp=${encodeURIComponent(cp)}&scope=national` : `?cp=${encodeURIComponent(cp)}`;

  try {
    const res = await fetch(`${endpoint}${query}`, { method: "GET", signal });
    if (!res.ok) return [];
    const data = (await res.json()) as Commune[] | { error: string };
    if (Array.isArray(data)) {
      cache.set(cacheKey, data);
      return data;
    }
    return [];
  } catch (err) {
    if ((err as { name?: string }).name === "AbortError") return [];
    console.error("[lookupCommunes] fetch error", err);
    return [];
  }
}
